"use client";

import { useState, useEffect, useRef } from "react";
import { Heading } from "./heading";
import { Subheading } from "./subheading";
import { cn } from "@/lib/utils";
import { InViewDiv } from "./in-view-div";
import { useMemo } from "react";
import { TestimonialColumnContainer } from "./testimonial-column-container";
import Image from "next/image";
import { ScrollShadow } from "@heroui/react";
import { useMediaQuery } from "usehooks-ts";
import { useInView } from "framer-motion";
export const Testimonials = () => {
  const [mounted, setMounted] = useState(false);
  const isMobileQuery = useMediaQuery("(max-width: 768px)");
  const mainContainerRef = useRef<HTMLDivElement>(null);

  // Only use the media query result after mounting to prevent hydration issues
  const isMobile = mounted ? isMobileQuery : false;
  const isInView = useInView(mainContainerRef, {
    margin: "0px 100px -50px 0px"
  });
  useEffect(() => {
    setMounted(true);
  }, []);
  return <div ref={mainContainerRef} className="relative z-20 py-10 md:py-20" data-sentry-component="Testimonials" data-sentry-source-file="testimonials.tsx">
      <Heading as="h2" data-sentry-element="Heading" data-sentry-source-file="testimonials.tsx">Built by founders, for founders</Heading>
      <Subheading className="mx-auto max-w-lg text-center" data-sentry-element="Subheading" data-sentry-source-file="testimonials.tsx">
        Don&apos;t just take my word for it. Here&apos;s what other founders
        achieved with these tools. No paid reviews, just real results. 👇
      </Subheading>
      <TestimonialGrid isMobile={isMobile} isInView={isInView} data-sentry-element="TestimonialGrid" data-sentry-source-file="testimonials.tsx" />
    </div>;
};
interface Testimonial {
  name: string;
  quote: string;
  src: string;
  designation?: string;
}
const testimonials = [{
  name: "Alex Chen",
  quote: "Saved me 3 weeks of back-and-forth with designers and namestorming sessions. Launched my SaaS in 2 days flat.",
  src: "https://i.pravatar.cc/150?img=1",
  designation: "Solo Founder"
}, {
  name: "Sarah Miller",
  quote: "The name generator alone is worth it. Got 'SwiftStack' in 5 mins after spending months trying to find the perfect name.",
  src: "https://i.pravatar.cc/150?img=2",
  designation: "Tech Founder"
}, {
  name: "James Wilson",
  quote: "Customer persona tool nailed our target market. We've doubled our conversion rate since using those insights.",
  src: "https://i.pravatar.cc/150?img=3",
  designation: "Growth Lead"
}, {
  name: "Lauren Foster",
  quote: "First giveaway brought in 2,000 signups in 48 hours. No ads, no cold emails, just pure viral growth.",
  src: "https://i.pravatar.cc/150?img=4",
  designation: "Marketing Director"
}, {
  name: "Rachel Martinez",
  quote: "From zero to a working MVP with actual users in one weekend. This is what founder tools should be like.",
  src: "https://i.pravatar.cc/150?img=5",
  designation: "Startup Founder"
}, {
  name: "Lisa Zhang",
  quote: "Finally, tools that actually deliver on the 'launch faster' promise. No fluff, just results.",
  src: "https://i.pravatar.cc/150?img=6",
  designation: "Product Manager"
}, {
  name: "David Kim",
  quote: "The logo maker saved us $1,500 and countless revisions. Looks just as good as custom design work.",
  src: "https://i.pravatar.cc/150?img=7",
  designation: "Bootstrap Founder"
}, {
  name: "Rachel Foster",
  quote: "Our viral campaign hit 10k shares in 24 hours. The templates and automation made it stupidly simple.",
  src: "https://i.pravatar.cc/150?img=8",
  designation: "Growth Marketer"
}, {
  name: "Jennifer Moore",
  quote: "Generated 50+ name ideas in one go. Each one better than what I could come up with after days of brainstorming.",
  src: "https://i.pravatar.cc/150?img=9",
  designation: "First-Time Founder"
}, {
  name: "Emma Rodriguez",
  quote: "The customer insights tool is like having a full-time market researcher on your team. Game-changer for positioning.",
  src: "https://i.pravatar.cc/150?img=10",
  designation: "Brand Strategist"
}, {
  name: "Chris Park",
  quote: "Went from idea to launched product in 72 hours. My competitors are still stuck in planning mode.",
  src: "https://i.pravatar.cc/150?img=11",
  designation: "Serial Entrepreneur"
}, {
  name: "Nina Patel",
  quote: "Our giveaway campaign got featured on Product Hunt. The viral mechanics just worked perfectly.",
  src: "https://i.pravatar.cc/150?img=41",
  designation: "Community Manager"
}, {
  name: "Ryan O'Connor",
  quote: "The logo designs are seriously impressive. Used one straight out of the generator for our pitch deck.",
  src: "https://i.pravatar.cc/150?img=13",
  designation: "Startup CEO"
}, {
  name: "William Parker",
  quote: "Best investment for my side hustle. Saved weeks of work and launched way faster than expected.",
  src: "https://i.pravatar.cc/150?img=14",
  designation: "Side Project Creator"
}, {
  name: "Marcus Johnson",
  quote: "Customer persona tool predicted our ideal target market perfectly. Saved us months of trial and error.",
  src: "https://i.pravatar.cc/150?img=15",
  designation: "Product Owner"
}, {
  name: "Sarah Mitchell",
  quote: "The viral tools helped us get our first 1,000 users without spending a dime on ads. Pure organic growth.",
  src: "https://i.pravatar.cc/150?img=16",
  designation: "Growth Hacker"
}, {
  name: "Daniel Lee",
  quote: "Generated our entire brand kit in one afternoon. Logo, name, and target audience - all spot on.",
  src: "https://i.pravatar.cc/150?img=17",
  designation: "Digital Founder"
}, {
  name: "Thomas Wright",
  quote: "The giveaway feature paid for itself in the first hour. Easiest marketing win we've ever had.",
  src: "https://i.pravatar.cc/150?img=18",
  designation: "Marketing Lead"
}, {
  name: "Kevin Zhang",
  quote: "Found our perfect business name in the first batch of suggestions. Domain was available too. No brainer.",
  src: "https://i.pravatar.cc/150?img=19",
  designation: "Tech Entrepreneur"
}, {
  name: "Hannah Brooks",
  quote: "Launched our MVP in a weekend and had real users by Monday. That's the speed founders need.",
  src: "https://i.pravatar.cc/150?img=20",
  designation: "Indie Maker"
}, {
  name: "Amanda Wilson",
  quote: "The viral growth tools are pure gold. Each share brings in 3-4 new signups consistently.",
  src: "https://i.pravatar.cc/150?img=21",
  designation: "Growth Specialist"
}, {
  name: "Jessica Wong",
  quote: "Everything just works. No learning curve, no complexity - just launch and grow. Exactly what I needed.",
  src: "https://i.pravatar.cc/150?img=22",
  designation: "SaaS Founder"
}];
function Testimonial({
  name,
  quote,
  src,
  designation,
  className,
  ...props
}: Omit<React.ComponentPropsWithoutRef<"figure">, keyof Testimonial> & Testimonial) {
  let animationDelay = useMemo(() => {
    let possibleAnimationDelays = ["0s", "0.1s", "0.2s", "0.3s", "0.4s", "0.5s"];
    return possibleAnimationDelays[Math.floor(Math.random() * possibleAnimationDelays.length)];
  }, []);
  const boxStyle = {};
  return <figure className={cn("shadow-derek animate-fade-in rounded-3xl bg-transparent p-8 opacity-0 dark:bg-neutral-900", className)} style={{
    animationDelay
  }} {...props} data-sentry-component="Testimonial" data-sentry-source-file="testimonials.tsx">
      <div className="flex flex-col items-start">
        <div className="flex gap-2">
          <Image src={src} width={150} height={150} className="h-10 w-10 rounded-full" alt={name} data-sentry-element="Image" data-sentry-source-file="testimonials.tsx" />
          <div>
            <h3 className="text-sm  font-medium text-neutral-500 dark:text-neutral-300">
              {name}
            </h3>
            <p className="text-sm font-normal text-neutral-500 dark:text-neutral-300">
              {designation}
            </p>
          </div>
        </div>
        <p className="dark:text-muted-dark mt-4 text-base text-muted">
          {quote}
        </p>
      </div>
    </figure>;
}
function TestimonialColumn({
  testimonials,
  className,
  containerClassName,
  shift = 0,
  disableAnimations = false
}: {
  testimonials: Testimonial[];
  className?: string;
  containerClassName?: (reviewIndex: number) => string;
  shift?: number;
  disableAnimations?: boolean;
}) {
  return <TestimonialColumnContainer className={cn(className)} shift={shift} disableAnimations={disableAnimations} data-sentry-element="TestimonialColumnContainer" data-sentry-component="TestimonialColumn" data-sentry-source-file="testimonials.tsx">
      {testimonials.concat(testimonials).map((testimonial, testimonialIndex) => <ScrollShadow key={testimonialIndex}>
            <Testimonial name={testimonial.name} quote={testimonial.quote} src={testimonial.src} designation={testimonial.designation} key={testimonialIndex} className={containerClassName?.(testimonialIndex % testimonials.length)} />
          </ScrollShadow>)}
    </TestimonialColumnContainer>;
}
function splitArray<T>(array: Array<T>, numParts: number) {
  let result: Array<Array<T>> = [];
  for (let i = 0; i < array.length; i++) {
    let index = i % numParts;
    if (!result[index]) {
      result[index] = [];
    }
    result[index].push(array[i]);
  }
  return result;
}
function TestimonialGrid({
  isMobile,
  isInView
}: {
  isMobile: boolean;
  isInView: boolean;
}) {
  let columns = splitArray(testimonials, 3);
  let column1 = columns[0];
  let column2 = columns[1];
  let column3 = splitArray(columns[2], 2);
  return <InViewDiv className="relative -mx-4 mt-16 grid h-[49rem] max-h-[150vh] grid-cols-1 items-start gap-8 overflow-hidden px-4 sm:mt-20 md:grid-cols-2 lg:grid-cols-3" data-sentry-element="InViewDiv" data-sentry-component="TestimonialGrid" data-sentry-source-file="testimonials.tsx">
      <TestimonialColumn testimonials={[...column1, ...column3.flat(), ...column2]} containerClassName={tIndex => cn(tIndex >= column1.length + column3[0].length && "md:hidden", tIndex >= column1.length && "lg:hidden")} shift={isMobile ? 5 : 10} disableAnimations={isMobile || !isInView} data-sentry-element="TestimonialColumn" data-sentry-source-file="testimonials.tsx" />
      <TestimonialColumn testimonials={[...column2, ...column3[1]]} className="hidden md:block" containerClassName={tIndex => tIndex >= column2.length ? "lg:hidden" : ""} shift={isMobile ? 7 : 15} disableAnimations={isMobile || !isInView} data-sentry-element="TestimonialColumn" data-sentry-source-file="testimonials.tsx" />
      <TestimonialColumn testimonials={column3.flat()} className="hidden lg:block" shift={isMobile ? 5 : 10} disableAnimations={isMobile || !isInView} data-sentry-element="TestimonialColumn" data-sentry-source-file="testimonials.tsx" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-white dark:from-[#09090B]" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-white dark:from-[#09090B]" />
    </InViewDiv>;
}